import React, { useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { MOBILE_CHART_BREAKPOINT } from '../../utils/chartResponsive';

const DEFAULT_SCROLL_HINT = 'برای مشاهده ستون‌های بیشتر، جدول را به چپ و راست بکشید';

interface HorizontalTableShellProps {
  minWidth: string | number;
  children: React.ReactNode;
  scrollHint?: string;
  showScrollHint?: boolean;
  className?: string;
  panelClassName?: string;
  scrollClassName?: string;
  scrollStyle?: React.CSSProperties;
}

/**
 * Horizontal scroll wrapper for wide RTL tables on mobile/tablet.
 * Uses dir="ltr" on the scroll shell so iOS Safari horizontal pan works reliably.
 */
const HorizontalTableShell: React.FC<HorizontalTableShellProps> = ({
  minWidth,
  children,
  scrollHint = DEFAULT_SCROLL_HINT,
  showScrollHint = true,
  className = '',
  panelClassName = '',
  scrollClassName = '',
  scrollStyle,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const leftShadow = container.querySelector('.scroll-shadow-left') as HTMLElement | null;
    const rightShadow = container.querySelector('.scroll-shadow-right') as HTMLElement | null;

    if (leftShadow && rightShadow) {
      leftShadow.style.opacity = scrollLeft > 10 ? '1' : '0';
      rightShadow.style.opacity = scrollLeft >= scrollWidth - clientWidth - 10 ? '0' : '1';
    }
  }, []);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    // RTL tables: start at the natural reading edge when horizontal scroll is needed
    if (window.matchMedia(`(max-width: ${MOBILE_CHART_BREAKPOINT}px)`).matches && maxScroll > 0) {
      container.scrollLeft = maxScroll;
    } else {
      container.scrollLeft = 0;
    }
    handleScroll();
  }, [handleScroll, minWidth]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll, minWidth]);

  const minWidthValue = typeof minWidth === 'number' ? `${minWidth}px` : minWidth;

  return (
    <div
      className={`mobile-table-panel rounded-lg border ${panelClassName}`}
      style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-card)' }}
    >
      {showScrollHint && (
        <div className="block lg:hidden bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-4 py-2">
          <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
            <svg className="w-4 h-4 shrink-0 animate-loading-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            <span className="text-sm font-medium">{scrollHint}</span>
            <svg className="w-4 h-4 shrink-0 animate-loading-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        className={`table-scroll-shell scrollbar-thin relative ${className} ${scrollClassName}`.trim()}
        dir="ltr"
        role="region"
        aria-label={scrollHint}
        tabIndex={0}
        style={scrollStyle}
      >
        <div
          className="absolute top-0 left-0 w-8 h-full bg-gradient-to-r from-white/80 to-transparent dark:from-gray-900/80 z-10 pointer-events-none lg:hidden scroll-shadow-left opacity-0 transition-opacity"
          aria-hidden="true"
        />
        <div
          className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-white/80 to-transparent dark:from-gray-900/80 z-10 pointer-events-none lg:hidden scroll-shadow-right opacity-100 transition-opacity"
          aria-hidden="true"
        />
        <div
          className="table-scroll-inner"
          dir="rtl"
          /* max(100%, …) keeps desktop fill; fixed floor enables mobile horizontal scroll.
             Inline minWidth alone overrode CSS min-width:100% and left empty space in LTR shells. */
          style={{ minWidth: `max(100%, ${minWidthValue})` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default HorizontalTableShell;
